import { VageConfig } from '../drag'
import DragAdapterInterface from './interfaces/adapters'
import DragCopy from './adapters/copy'
import DragRegular from './adapters/regular'
import { Position } from './interfaces/general'

export interface VageDragEvent {
  target: HTMLElement | Window
  x: number
  y: number
}
export interface VageDrag {
  deActivate(): void
  on(event: string, callback: ((event: VageDragEvent) => void)): void
}

export const vageDragEvents = {
  START: 'start',
  DROP: 'drop',
  DRAG: 'drag',
}

interface Binding {
  events: string[]
  listener: string
  target: HTMLElement | Document
}

export default class Manager implements VageDrag {
  [key: string]: any

  private el: HTMLElement
  private callbacks: Map<string, ((event: VageDragEvent) => void)> = new Map()
  private bindings: Map<string, EventListenerOrEventListenerObject> = new Map()
  private config: VageConfig
  private adapter: DragAdapterInterface

  constructor(el: HTMLElement, config: VageConfig = {}) {
    this.el = el
    this.config = config
    this.adapter = this.config.moveToTop === false ? new DragRegular(this.el) : new DragCopy(this.el)
    this.activate()
  }

  public get element(): HTMLElement {
    return this.el
  }

  public activate() {
    this.bind([
      {
        events: ['mousedown', 'touchstart'],
        listener: 'onMouseDown',
        target: this.el,
      },
    ])
  }

  public deActivate() {
    this.unbind([
      {
        events: ['mousedown', 'touchstart'],
        listener: 'onMouseDown',
        target: this.el,
      },
    ])
  }

  public on(event: string, callback: (event: VageDragEvent) => void) {
    this.callbacks.set(event, callback)
  }

  private bind(bindings: Binding[]) {
    bindings.forEach((binding: Binding) => {
      binding.events.forEach((event: string) => {
        this.bindings.set(event, this[binding.listener].bind(this))
        const listener = this.bindings.get(event) as EventListenerOrEventListenerObject
        binding.target.addEventListener(event, listener)
      })
    })
  }

  private unbind(bindings: Binding[]) {
    bindings.forEach((binding: Binding) => {
      binding.events.forEach((event: string) => {
        const listener = this.bindings.get(event) as EventListenerOrEventListenerObject
        binding.target.removeEventListener(event, listener)
        this.bindings.delete(event)
      })
    })
  }

  private onMouseDown(event: MouseEvent) {
    event.preventDefault()
    const position: Position = this.adapter.onMouseDown(event)

    this.bind([
      {
        events: ['mouseup', 'touchend'],
        listener: 'onMouseUp',
        target: document,
      },
      {
        events: ['mousemove', 'touchmove'],
        listener: 'onMouseMove',
        target: document,
      },
    ])

    document.body.classList.add('is-dragging')
    this.emit(vageDragEvents.START, position)
  }

  private onMouseMove(event: any) {
    const position: Position = this.adapter.onMouseMove(event)
    this.emit(vageDragEvents.DRAG, position)
  }

  private onMouseUp(event: any) {
    this.unbind([
      {
        events: ['mouseup', 'touchend'],
        listener: 'onMouseUp',
        target: document,
      },
      {
        events: ['mousemove', 'touchmove'],
        listener: 'onMouseMove',
        target: document,
      },
    ])

    const position: Position = this.adapter.onMouseUp(event)
    this.emit(vageDragEvents.DROP, position)
    document.body.classList.remove('is-dragging')
  }

  private emit(eventName: string, position: Position) {
    const callback = this.callbacks.get(eventName)

    if (callback) {
      callback.call(this, {
        target: this.el,
        x: position.x,
        y: position.y,
      })
    }
  }
}
